import { useRef, useEffect, useCallback } from "react";
import type { CurrencyInfo } from "../api/mockData";

interface CurrencyRowProps {
  currency: CurrencyInfo;
  value: string;
  isBase: boolean;
  staggerIndex: number;
  onFocus: (code: string) => void;
  onChange: (code: string, value: string) => void;
}

export function CurrencyRow({
  currency,
  value,
  isBase,
  staggerIndex,
  onFocus,
  onChange,
}: CurrencyRowProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isBase && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isBase]);

  const handleClick = useCallback(() => {
    onFocus(currency.code);
    // Select all text on click for easy replacement
    setTimeout(() => inputRef.current?.select(), 0);
  }, [currency.code, onFocus]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      // Allow empty, digits, one decimal point
      if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
        onChange(currency.code, raw);
      }
    },
    [currency.code, onChange]
  );

  const handleFocus = useCallback(() => {
    onFocus(currency.code);
    inputRef.current?.select();
  }, [currency.code, onFocus]);

  return (
    <div
      onClick={handleClick}
      className={`
        animate-fade-in-up stagger-${staggerIndex}
        group relative flex items-center gap-4 px-5 py-4 rounded-xl cursor-pointer
        transition-all duration-300 ease-out
        ${
          isBase
            ? "bg-[var(--color-teal-light)] border-2 border-[var(--color-teal)] shadow-[var(--shadow-active)]"
            : "bg-[var(--card)] border-2 border-transparent hover:border-[var(--border)] shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-card-hover)]"
        }
      `}
    >
      {/* Flag + Currency info */}
      <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
        <span className="text-3xl leading-none select-none" role="img" aria-label={currency.name}>
          {currency.flag}
        </span>
        <div className="flex flex-col">
          <span
            className={`text-base font-semibold tracking-tight ${isBase ? "text-[var(--color-teal-dark)]" : "text-[var(--color-ink)]"}`}
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            {currency.code}
          </span>
          <span
            className="text-xs text-[var(--color-ink-light)] hidden sm:block"
            style={{ fontFamily: "var(--font-mono)", fontWeight: 300 }}
          >
            {currency.name}
          </span>
        </div>
      </div>

      {/* Symbol */}
      <span
        className={`ml-auto text-lg font-light ${isBase ? "text-[var(--color-teal)]" : "text-[var(--color-ink-light)]"}`}
        style={{ fontFamily: "var(--font-mono)" }}
      >
        {currency.symbol}
      </span>

      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder="0"
        aria-label={`Amount in ${currency.name}`}
        className={`
          w-[140px] sm:w-[180px] text-right text-xl sm:text-2xl font-medium
          bg-transparent outline-none border-none
          placeholder:text-[var(--color-ink-light)]/30
          transition-colors duration-200
          ${isBase ? "text-[var(--color-teal-dark)]" : "text-[var(--color-ink)]"}
        `}
        style={{ fontFamily: "var(--font-mono)", fontWeight: 500 }}
      />

      {/* Active indicator bar */}
      {isBase && (
        <div className="absolute left-0 top-3 bottom-3 w-1 rounded-full bg-[var(--color-teal)]" />
      )}
    </div>
  );
}
